import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  Botao,
  Cartao,
  Selo,
  Tabela,
  formatarMoeda,
  formatarPercentual,
  useDialogo,
  type ColunaTabela,
  type TomSemantico,
} from './ui';
import { ErroDaApi } from '../paginas/ErroDaApi';
import { useEmpresaCorrente } from '../hooks/useSessao';
import {
  CHAVE_DOS_PEDIDOS,
  ROTULO_DA_SITUACAO_DO_PEDIDO,
  atenderPedido,
  cancelarPedido,
  listarPedidos,
  type PedidoDeFechamento,
  type ServicoComPedido,
  type SituacaoDoPedido,
} from '../servicos/participacao';

/**
 * Pedidos de fechamento do assessor, nas duas pontas.
 *
 * O assessor simula para o cliente dele e **pede**; a operação monta o
 * contrato, colhe assinaturas, protocola — e marca o pedido como atendido.
 * `MeusPedidos` é o lado do assessor (acompanhar e cancelar o que está
 * aberto); `PedidosDaOperacao`, o do admin (os abertos, com os números que o
 * assessor viu).
 *
 * Os dois somem quando não há pedido: a tela não ganha um quadro vazio.
 */

const TOM_DA_SITUACAO: Readonly<Record<SituacaoDoPedido, TomSemantico>> = {
  ABERTA: 'atencao',
  ATENDIDA: 'positivo',
  CANCELADA: 'neutro',
};

/** Os números do pedido numa linha — os mesmos que o assessor viu ao simular. */
export function resumoDoPedido(p: PedidoDeFechamento): string {
  const entradas = (p.simulacao?.['entradas'] ?? {}) as Record<string, unknown>;
  const numero = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;
  const partes: string[] = [];

  if (p.negociacaoPontualId !== null) partes.push('Simulação gravada pela operação');
  if (p.contratoPassivoId !== null) partes.push('Contrato em simulação');

  if (p.servico === 'PONTUAL') {
    const face = numero(entradas['valorFace']);
    const compra = numero(entradas['desagioCompraPercentual']);
    const venda = numero(entradas['desagioVendaPercentual']);
    const parcelas = numero(entradas['parcelas']);
    if (face !== null) partes.push(`Face ${formatarMoeda(face)}`);
    if (compra !== null && venda !== null) {
      partes.push(
        `deságio ${formatarPercentual(compra)} compra, ${formatarPercentual(venda)} venda`,
      );
    }
    if (parcelas !== null) partes.push(parcelas === 1 ? 'à vista' : `${parcelas} parcelas`);
  } else {
    const divida = numero(entradas['valorDivida']);
    const desagio = numero(entradas['percentualDesagio']);
    const parcelas = numero(entradas['quantidadeParcelas']);
    const taxa = numero(entradas['taxaJurosMensal']);
    if (divida !== null) partes.push(`Dívida ${formatarMoeda(divida)}`);
    if (desagio !== null) partes.push(`deságio ${formatarPercentual(desagio)}`);
    if (parcelas !== null) {
      const sistema = entradas['indice'] === 'price' ? 'Price' : 'SAC';
      partes.push(
        `${parcelas} parcelas ${sistema}${taxa === null ? '' : `, ${formatarPercentual(taxa)} a.m.`}`,
      );
    }
  }
  return partes.length === 0 ? '—' : partes.join(' · ');
}

/** Depois de pedir, atender ou cancelar: as listas, as marcas "fechamento pedido" e o sino. */
export function invalidarPedidos(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: CHAVE_DOS_PEDIDOS });
  void queryClient.invalidateQueries({ queryKey: ['pontual-da-parte'] });
  void queryClient.invalidateQueries({ queryKey: ['passivo-da-parte'] });
  void queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
}

const emBr = (iso: string): string => new Date(iso).toLocaleDateString('pt-BR');

function Situacao(props: { readonly pedido: PedidoDeFechamento }): JSX.Element {
  const p = props.pedido;
  return (
    <span className="flex flex-col gap-1">
      <Selo tom={TOM_DA_SITUACAO[p.status]}>{ROTULO_DA_SITUACAO_DO_PEDIDO[p.status]}</Selo>
      {p.resolvidaEm !== null && (
        <span className="text-xs text-conteudo-sutil">em {emBr(p.resolvidaEm)}</span>
      )}
    </span>
  );
}

function OQueFoiPedido(props: { readonly pedido: PedidoDeFechamento }): JSX.Element {
  const p = props.pedido;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-sm text-conteudo-secundario">{resumoDoPedido(p)}</span>
      {p.observacao !== null && (
        <span className="text-xs text-conteudo-sutil">“{p.observacao}”</span>
      )}
    </span>
  );
}

/** O lado do assessor: o que a assessoria escolhida pediu, e em que pé está. */
export function MeusPedidos(props: { readonly servico: ServicoComPedido }): JSX.Element | null {
  const empresa = useEmpresaCorrente();
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();

  const pedidos = useQuery({
    queryKey: [...CHAVE_DOS_PEDIDOS, props.servico, empresa],
    queryFn: () => listarPedidos({ servico: props.servico, empresa: empresa ?? '' }),
    enabled: empresa !== null,
  });

  const cancelar = useMutation({
    mutationFn: cancelarPedido,
    onSuccess: () => invalidarPedidos(queryClient),
  });

  const lista = pedidos.data ?? [];
  if (pedidos.isError) {
    return <ErroDaApi titulo="Não foi possível carregar os pedidos" erro={pedidos.error} />;
  }
  if (lista.length === 0) return null;

  const colunas: readonly ColunaTabela<PedidoDeFechamento>[] = [
    {
      chave: 'cliente',
      titulo: 'Cliente',
      larguraMinima: '14rem',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="text-sm text-conteudo-primario">{p.clienteNome ?? 'Cliente'}</span>
          <span className="text-xs text-conteudo-sutil">
            {p.solicitadoPorNome === null ? '' : `${p.solicitadoPorNome} · `}
            {emBr(p.criadaEm)}
          </span>
        </span>
      ),
    },
    {
      chave: 'pedido',
      titulo: 'O que foi pedido',
      larguraMinima: '18rem',
      celula: (p) => <OQueFoiPedido pedido={p} />,
    },
    {
      chave: 'status',
      titulo: 'Situação',
      larguraMinima: '11rem',
      celula: (p) => <Situacao pedido={p} />,
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '7rem',
      celula: (p) =>
        p.status === 'ABERTA' ? (
          <Botao
            variante="sutil"
            tamanho="pequeno"
            carregando={cancelar.isPending && cancelar.variables === p.id}
            onClick={() => {
              void (async () => {
                const ok = await confirmar({
                  titulo: 'Cancelar este pedido?',
                  descricao:
                    'A operação deixa de vê-lo como pendente. Dá para pedir de novo depois.',
                  rotuloConfirmar: 'Cancelar pedido',
                  rotuloCancelar: 'Voltar',
                });
                if (ok) cancelar.mutate(p.id);
              })();
            }}
          >
            Cancelar
          </Botao>
        ) : (
          <span className="sr-only">Pedido resolvido</span>
        ),
    },
  ];

  return (
    <Cartao
      como="section"
      titulo="Meus pedidos de fechamento"
      subtitulo="O que a sua assessoria pediu à operação. Atendido: a operação registrou o fechamento — o andamento segue na lista acima."
      corpoSemPreenchimento
    >
      {cancelar.isError && <ErroDaApi titulo="Não foi possível cancelar" erro={cancelar.error} />}
      <Tabela
        legenda="Pedidos de fechamento da assessoria"
        densidade="compacta"
        colunas={colunas}
        dados={lista}
        chaveDaLinha={(p) => p.id}
        chaveDeCabecalhoDaLinha="cliente"
      />
    </Cartao>
  );
}

/**
 * O lado da operação: os pedidos abertos de um serviço.
 *
 * `aoAbrir` leva ao registro do pedido — a negociação gravada ou o contrato
 * em simulação, ou o cliente, quando o pedido é de uma simulação livre. Atender
 * é dizer ao assessor que o fechamento está encaminhado.
 */
export function PedidosDaOperacao(props: {
  readonly servico: ServicoComPedido;
  readonly aoAbrir?: (pedido: PedidoDeFechamento) => void;
  /** O texto do botão que leva ao pedido. */
  readonly rotuloAbrir?: (pedido: PedidoDeFechamento) => string;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();

  const pedidos = useQuery({
    queryKey: [...CHAVE_DOS_PEDIDOS, props.servico, 'ABERTA'],
    queryFn: () => listarPedidos({ servico: props.servico, status: 'ABERTA' }),
  });

  const atender = useMutation({
    mutationFn: atenderPedido,
    onSuccess: () => invalidarPedidos(queryClient),
  });
  const cancelar = useMutation({
    mutationFn: cancelarPedido,
    onSuccess: () => invalidarPedidos(queryClient),
  });

  const lista = pedidos.data ?? [];
  if (pedidos.isError) {
    return <ErroDaApi titulo="Não foi possível carregar os pedidos" erro={pedidos.error} />;
  }
  if (lista.length === 0) return null;

  const colunas: readonly ColunaTabela<PedidoDeFechamento>[] = [
    {
      chave: 'cliente',
      titulo: 'Cliente e assessor',
      larguraMinima: '16rem',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="text-sm text-conteudo-primario">{p.clienteNome ?? 'Cliente'}</span>
          <span className="text-xs text-conteudo-sutil">
            assessor {p.assessorNome ?? '—'}
            {p.solicitadoPorNome === null ? '' : ` · ${p.solicitadoPorNome}`} · {emBr(p.criadaEm)}
          </span>
        </span>
      ),
    },
    {
      chave: 'pedido',
      titulo: 'O que foi pedido',
      larguraMinima: '18rem',
      celula: (p) => <OQueFoiPedido pedido={p} />,
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '20rem',
      celula: (p) => (
        <span className="flex flex-wrap items-center justify-end gap-2">
          {props.aoAbrir !== undefined && (
            <Botao variante="secundario" tamanho="pequeno" onClick={() => props.aoAbrir?.(p)}>
              {props.rotuloAbrir?.(p) ?? 'Abrir'}
            </Botao>
          )}
          <Botao
            tamanho="pequeno"
            carregando={atender.isPending && atender.variables === p.id}
            onClick={() => {
              void (async () => {
                const ok = await confirmar({
                  titulo: 'Marcar como atendido?',
                  descricao:
                    'Use depois de montar a negociação ou o contrato com os números do pedido. O assessor passa a vê-lo como atendido.',
                  rotuloConfirmar: 'Marcar como atendido',
                });
                if (ok) atender.mutate(p.id);
              })();
            }}
          >
            Atendido
          </Botao>
          <Botao
            variante="sutil"
            tamanho="pequeno"
            carregando={cancelar.isPending && cancelar.variables === p.id}
            onClick={() => {
              void (async () => {
                const ok = await confirmar({
                  titulo: 'Cancelar este pedido?',
                  descricao: 'O pedido sai da fila. O assessor o vê como cancelado.',
                  rotuloConfirmar: 'Cancelar pedido',
                  rotuloCancelar: 'Voltar',
                  tom: 'perigo',
                });
                if (ok) cancelar.mutate(p.id);
              })();
            }}
          >
            Cancelar
          </Botao>
        </span>
      ),
    },
  ];

  return (
    <Cartao
      como="section"
      titulo={`Pedidos de fechamento dos assessores (${lista.length})`}
      subtitulo="O assessor simulou para o cliente dele e pediu o fechamento. Monte com os números do pedido e marque como atendido."
      corpoSemPreenchimento
    >
      {(atender.isError || cancelar.isError) && (
        <ErroDaApi titulo="Não foi possível" erro={atender.error ?? cancelar.error} />
      )}
      <Tabela
        legenda="Pedidos de fechamento abertos"
        densidade="compacta"
        colunas={colunas}
        dados={lista}
        chaveDaLinha={(p) => p.id}
        chaveDeCabecalhoDaLinha="cliente"
      />
    </Cartao>
  );
}
